import { type ReactElement } from 'react';
import compact from 'lodash/compact';
import type { MountedStorageStatus } from '@frameio/graphql-client/generated';
import { Button2, Select, Text, Tooltip } from '@frameio/vapor';
import type { IconProps } from '@frameio/vapor-icons';
import { IconChevronDown } from '@frameio/vapor-icons';
import { MountedStorageIcon, StandardStorageIcon } from '../../icons';
import TEXT from './generated/TEXT/MountedStorageTypeSelect';
import styles from './MountedStorageTypeSelect.module.css';

TEXT.DEFINE({
  /** Setting title for mounted storage */
  title: 'Storage Type',
  /** Label for storage type Standard Storage */
  standardStorageLabel: 'Standard Storage',
  /** Trigger label for storage types (short label) */
  triggerLabel: {
    /** Trigger label for storage type Mounted Storage (short label) */
    mounted: 'Mounted',
    /** Trigger label for storage type Standard Storage (short label) */
    standard: 'Standard',
  },
  /** Description for storage type Standard Storage */
  standardStorageDescription:
    'Store this project in the cloud only and download to edit.',
  /** Label for storage type Mounted Storage */
  mountedStorageLabel: 'Mounted Storage',
  /** Description for storage type Mounted Storage */
  mountedStorageDescription:
    'Store this project in the cloud and stream to your desktop using Frame.io Drive',
  /** Error message for upgrade issues */
  upgradeError:
    'Not enough Mounted Storage available. Free up space or add capacity to mount this project.',
});

type StorageType = 'standard' | 'mounted';

type MountedStorageSelectOption = {
  description: string;
  iconBefore: ReactElement<IconProps>;
  label: string;
  value: StorageType;
};

type MountedStorageTypeSelectProps = {
  disableUpgrade: boolean;
  isDisabled: boolean;
  isFullSize?: boolean;
  label?: string;
  setStatus: (status: MountedStorageStatus) => void;
  status: MountedStorageStatus;
};

const getSelectOptions = (): MountedStorageSelectOption[] => [
  {
    description: TEXT.standardStorageDescription,
    iconBefore: <StandardStorageIcon size={24} />,
    label: TEXT.standardStorageLabel,
    value: 'standard',
  },
  {
    description: TEXT.mountedStorageDescription,
    iconBefore: <MountedStorageIcon size={24} />,
    label: TEXT.mountedStorageLabel,
    value: 'mounted',
  },
];

export const MountedStorageTypeSelect = ({
  disableUpgrade,
  isDisabled,
  isFullSize,
  label,
  setStatus,
  status,
}: MountedStorageTypeSelectProps) => {
  const selectedValue: StorageType =
    status === 'ENABLED' ? 'mounted' : 'standard';

  const selectOptions = getSelectOptions();
  const selected =
    selectOptions.find((v) => v.value === selectedValue) ?? selectOptions[0];

  const handleChange = (value: StorageType) => {
    setStatus(value === 'standard' ? 'DISABLED' : 'ENABLED');
  };

  return (
    <Tooltip disabled={!disableUpgrade} title={TEXT.upgradeError}>
      <div
        className={compact([
          styles.container,
          isFullSize && styles.fullSize,
        ]).join(' ')}
      >
        <Select
          content={selectOptions}
          isDisabled={disableUpgrade || isDisabled}
          name="mounted-storage-type-select"
          onChange={handleChange}
          shouldUsePortal={isFullSize ? false : undefined}
          value={selectedValue}
        >
          {({ selectedOption }) => (
            <Button2
              iconAfter={
                <IconChevronDown
                  className={styles.chevronIcon}
                  color="var(--colors-icon-secondary)"
                  size={isFullSize ? 24 : 16}
                />
              }
              iconBefore={isFullSize ? selected.iconBefore : undefined}
              size={isFullSize ? 'regular' : 'small'}
              variant={isFullSize ? 'tertiary' : 'secondary'}
            >
              <Text
                color={isDisabled ? 'disabled' : 'primary'}
                enableTooltip={true}
                truncate={true}
                variant="body"
              >
                {label ||
                  (isFullSize
                    ? selectedOption.label
                    : TEXT.triggerLabel[selectedOption.value as StorageType])}
              </Text>
            </Button2>
          )}
        </Select>
      </div>
    </Tooltip>
  );
};
